// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportBody.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import connect from 'share/enhancers/Izenda.Connect';
import { observable, action, reaction } from 'mobx';
import PropTypes from 'prop-types';
import { get, flow, sortBy, some, find, filter, forEach, getOr } from 'lodash/fp';

import Panel from 'share/components/controls/Izenda.Panel';
import GridView from 'share/components/controls/GridLayout/Izenda.GridView';
import GridItem from 'share/components/controls/GridLayout/Izenda.GridItem';
import Dimensions from 'share/components/hoc/Izenda.Dimensions';
import ReportPartContainer from './Izenda.ReportPartContainer';
import ReportBodyLayoutUIStore from './Izenda.ReportBodyLayoutUIStore';
import CONSTANTS from 'logic/data/Izenda.ReportBodyConstants';
import PropertiesEditorManager from 'modules/Izenda.PropertiesEditorManager';
import ReportBodyHeader from './Izenda.ReportBodyHeader';
import classnames from 'classnames';

const SCROLLBAR_HEIGHT = 8;
const SCROLLBAR_PADDING = 2;

@connect(Dimensions)
export default class ReportBody extends Component {
  @observable gridHeight = null;

  static propTypes = {
    store: PropTypes.object,
    fullSize: PropTypes.bool,
    infinityRows: PropTypes.bool,
    infinityCols: PropTypes.bool,
    exportFormatSetting: PropTypes.object,
    hasOuterScroll: PropTypes.bool,
    getOuterScrollTop: PropTypes.func
  };

  static defaultProps = {
    fullSize: false,
    infinityRows: true,
    infinityCols: true
  };

  constructor(props: any) {
   
    super(props);
    const { width, fullSize, infinityRows, infinityCols } = this.props;

    this.shouldNotUpdateComponent = false;
    this.layoutStore = new ReportBodyLayoutUIStore(width, fullSize, infinityRows, infinityCols);
    this.setFullHeightOnly();
  }

  shouldComponentUpdate(nextProps: any) {
    if (this.shouldNotUpdateComponent) {
      this.shouldNotUpdateComponent = false;
      return false;
    }
    return this.props.reportBodyPanelState === nextProps.reportBodyPanelState;
  }

  componentWillReceiveProps(nextProps: any) {
    const { width, fullSize, headerPanelState, titleDescriptionPanelState, reportBodyPanelState } = this.props;
    if (nextProps.width !== width || nextProps.fullSize !== fullSize) {
      this.layoutStore.setViewWidth(nextProps.width);
      this.layoutStore.setAutoSize(nextProps.fullSize);
    }
    if (
      nextProps.headerPanelState !== headerPanelState ||
      nextProps.titleDescriptionPanelState !== titleDescriptionPanelState ||
      nextProps.reportBodyPanelState !== reportBodyPanelState
    ) {
      this.setGridHeight();
    }
    this.setFullHeightOnly();
  }

  componentDidMount() {
    this.props.finishRender && this.props.finishRender(this);
    this.addConfigReportPartActions();
    const dom = ReactDOM.findDOMNode(this);
    this.setGridHeight();
    this.layoutStore.setViewWidth(dom.clientWidth);
    const {
      store: { reportStore }
    } = this.props;
    const allowToSave = get('model.allowToSave', reportStore);
    allowToSave &&
      reportStore.alertDetails.set({
        success: true,
        message: CONSTANTS.MESSAGES.OUTDATED_REPORT
      });
  }

  componentDidUpdate() {
    this.props.finishRender && this.props.finishRender(this);
    this.setGridHeight();
  }

  componentWillUnmount() {
    this.props.finishRender && this.props.finishRender(null);
    this.configReportPartAction && this.configReportPartAction();
  }

  setGridView = (ref: any) => {
    this.gridView = ref;
  };

  setPartContainer = (ref: any) => {
    this.partContainer = ref;
  };

  setReportBody = (ref: any) => {
    this.reportBody = ref;
  };

  @action
  setFullHeightOnly() {
    this.props.viewMode && this.layoutStore.setFullHeightOnly(true);
  }

  @action
  handleChangeReportPartFullScreen = (reportPart: any) => {
    this.shouldNotUpdateComponent = true;

    const {
      store: { reportBody }
    } = this.props;
    reportBody.changeFullScreenMode(reportPart);

    const itemConfig = this.layoutStore.getLayoutItem(reportPart.id);
    if (itemConfig && itemConfig.isFullSize !== reportPart.isBackSide) itemConfig.isFullSize = reportPart.isBackSide;
  };

  @action
  setGridHeight() {
    const dom = ReactDOM.findDOMNode(this.gridView) || this.partContainer;
    if (dom) {
      const { bottom } = this.props;
      const { top } = dom.getBoundingClientRect();
      this.gridHeight = window.innerHeight - (top + bottom + this.props.getOuterScrollTop());
    }
  }

  /**
   * Add new report part at position
   * @param  {Object} offset New report part position
   */
  @action
  onAddItem = (offset: any) => {
    const {
      store: { reportBody }
    } = this.props;
    if (offset) {
      const { left, top, width, height } = offset;
      reportBody.addReportPart(left, top, width, height);
    }
  };

  @action
  onUpdateItemPositions = (newItemPos, manual) => {
    const needPartUpdate = part => {
      return some({ id: part.id }, newItemPos);
    };

    const setPosition = part => {
      const newPos = find({ id: part.id }, newItemPos);
      const { left, top, width, height } = newPos;
      part.setPosition(
        {
          positionX: left,
          positionY: top,
          width: width,
          height: height
        },
        manual
      );
    };

    const {
      store: {
        reportBody: { reportParts }
      }
    } = this.props;
    flow(
      filter(needPartUpdate),
      forEach(setPosition)
    )(reportParts);
  };

  /** Get scrollbox ref of grid view*/
  getGridViewScrollBox() {
    return this.gridView ? this.gridView.getScrollBox() : null;
  }

  addConfigReportPartActions() {
    const { store } = this.props;
    
    const scrollToConfigReportPart = () => {
      const layoutStore = this.layoutStore;
      const configReportPart = get('reportBody.configReportPart', store);
      const isBackSide = get('isBackSide', configReportPart);
      const layoutItem = layoutStore.getLayoutItem(configReportPart.id);
      if (!layoutItem || !configReportPart || isBackSide) {
        return;
      }

      this.gridView &&
        this.gridView.scrollTo({
          left: get('pxLeft', layoutItem),
          top: get('pxTop', layoutItem)
        });
    };

    const propsEditorId = PropertiesEditorManager.findEditorId([
      {
        name: 'reportPartProps'
      }
    ]);

    this.configReportPartAction = reaction(
      (): any => {
        const configReportPart = get('reportBody.configReportPart', store);
        const editorInfo = PropertiesEditorManager.getEditorInfoById(propsEditorId);
        const status = getOr({}, 'status', editorInfo);

        return {
          needScrollToConfigPart: !!configReportPart && status.reportPartProps && !configReportPart.isBackSide,
          configReportPart: configReportPart,
          configReportPartId: configReportPart && configReportPart.reportId
        };
      },
      (data: any): void => {
        if (!data.needScrollToConfigPart) {
          return;
        }

        // scroll to config report part
        // hack - in case user click on header to select report part
        // mouse-down event will be trigger to update selected-reprot part
        // and it will be conflicted to scroll-action
        setTimeout(function() {
          scrollToConfigReportPart();
        }, 200);
      }
    );
  }

  renderListReportPart() {
    const { props } = this;
    const {
      store,
      store: { reportBody }
    } = props;
    const activeReportParts = sortBy(['positionY', 'positionX'], reportBody.activeReportParts.slice());
    
    return activeReportParts.map(item => {
      const { isBackSide, isNew, id } = item;
      return (
        <GridItem
          key={id}
          id={id}
          moveToMe={isNew}
          isDraggable={!isBackSide}
          isResizable={!item.autoSize && !isBackSide}
          handle=".report-part-header"
          cancel=".btn, input"
        >
          <ReportPartContainer
            {...props}
            isSnapEditOn={this.layoutStore.isSnapEditOn}
            gridItem={item}
            store={store}
            reportBody={reportBody}
            onChangeFullScreen={this.handleChangeReportPartFullScreen}
            autoSize={item.autoSize}
          />
        </GridItem>
      );
    });
  }

  render() {
    
    const {
      quickEdit,
      viewMode,
      bottom,
      hasOuterScroll,
      store,
      store: { reportBody }
    } = this.props;
    const { snapToGrid, isFullScreen } = reportBody;
    const layoutStore = this.layoutStore;
    const layoutData = reportBody.activeReportParts.map(toReportPartLayoutItem);
    const header = {
      component: ReportBodyHeader,
      store
    };

    const reportBodyGridClass = classnames('report-body-grid', {
      'izenda-ReportBodyGrid-editable': !viewMode
    });
    const isBackSide = get('configReportPart.isBackSide', reportBody);
    const gridHeightValue = hasOuterScroll ? null : this.gridHeight;
    const partContainerStyle = isBackSide ? { height: gridHeightValue } : {};
    const stickyScrollBottom = hasOuterScroll ? bottom : null;
    const body =
      (isBackSide && !hasOuterScroll) || isFullScreen ? (
        <div className="izenda-ReportPart-Fullsize" ref={this.setPartContainer} style={partContainerStyle}>
          <ReportPartContainer
            store={store}
            reportBody={store.reportBody}
            gridItem={store.reportBody.configReportPart}
            onChangeFullScreen={this.handleChangeReportPartFullScreen}
          />
        </div>
      ) : (
        <GridView
          stickyScrollBottom={stickyScrollBottom}
          className={reportBodyGridClass}
          alwaysShowHorizontalScroll
          alwaysShowVerticalScroll
          gridHeight={gridHeightValue}
          ref={this.setGridView}
          editable={!viewMode}
          fullHeight={viewMode}
          allowAddInline={!quickEdit && !viewMode}
          layoutStore={layoutStore}
          layoutData={layoutData}
          snapToGrid={snapToGrid}
          onAddItem={this.onAddItem}
          onUpdateItemPositions={this.onUpdateItemPositions}
        >
          {this.renderListReportPart()}
        </GridView>
      );

    return quickEdit || viewMode ? (
      <div className="report-body">{body}</div>
    ) : (
      <Panel
        afterToggled={this.props.afterToggled}
        header={header}
        collapsible={true}
        initState={true}
        className="report-body"
        ref={this.setReportBody}
      >
        {body}
      </Panel>
    );
  }
}

function toReportPartLayoutItem(item: any) {
 
  return {
    id: item.id,
    x: item.positionX,
    y: item.positionY,
    width: item.width,
    height: item.height,
    isFullSize: item.isBackSide,
    autoSize: item.autoSize,
    isLoading() {
      return item.isLoadingData;
    }
  };
}



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/ReportBody/Izenda.ReportBody.js